import { useQuery } from '@tanstack/react-query';

import { useAuthContext } from '@/auth/useAuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { getNfseImpediments } from '@/services/invoice/impediments';
import { BACKGROUND_QUERY_META } from '@/utils/backgroundQuery';

export const NFSE_IMPEDIMENTS_QUERY_KEY = ['nfse-impediments'];

const NO_IMPEDIMENTS = [];

/**
 * O aviso é um extra do layout: falhar a consulta não pode virar o toast de erro
 * que o `onError` global das queries mostra (routes/index.js). Por isso o erro é
 * engolido aqui, e não num `onError` da query. 404 é o caso esperado enquanto a
 * rota do backend não está publicada; 403 é a impersonação que escapou do
 * `enabled`; rede caída não é assunto deste aviso.
 */
const fetchImpedimentsQuietly = async () => {
  try {
    return await getNfseImpediments();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      '[nfse-impediments] consulta indisponível',
      error?.response?.status ?? error?.code
    );
    return NO_IMPEDIMENTS;
  }
};

/**
 * Impedimentos de emissão de NFS-e do produtor logado, consultados uma vez ao
 * entrar no painel.
 *
 * Fica fora de quem não está na audiência de NFS-e (`nfseEnabled`, o mesmo
 * critério do `NfseFeatureGuard`): a ação do aviso levaria a uma rota que o guard
 * fecha com 403. Sob impersonação a view do backend recusa a chamada, então ela
 * nem sai.
 */
export default function useNfseImpediments() {
  const { user } = useAuthContext();
  const { impersonatedUserId } = useImpersonation();

  const enabled = Boolean(user?.nfseEnabled) && !impersonatedUserId;

  const { data, isFetched } = useQuery({
    queryKey: [...NFSE_IMPEDIMENTS_QUERY_KEY, user?.id],
    queryFn: fetchImpedimentsQuietly,
    enabled,
    staleTime: 5 * 60 * 1000,
    // Montada pelo header: sem a marca, a `RouteTransitionGate` seguraria toda
    // troca de tela até esta consulta voltar.
    meta: BACKGROUND_QUERY_META,
  });

  // `isDecided`: já se sabe se há impedimento. O coordenador de avisos espera por isso antes de
  // dar a vez a um aviso de prioridade menor.
  return {
    impediments: enabled && data ? data : NO_IMPEDIMENTS,
    isDecided: !enabled || isFetched,
  };
}
